import { useEffect, useMemo, useState } from 'react';
import { logger } from '../utils/logger';
import { parseEmbedParams } from '../utils/embedUtils';
import type { EmbedParams } from '../utils/embedUtils';

// Re-export types and constants so existing consumers don't break
export type { EmbedParams, EmbedParamKey } from '../utils/embedUtils';
export { EMBED_PARAM_KEYS } from '../utils/embedUtils';

/**
 * Parsed embed parameters with proper types
 */
export interface ParsedEmbedParams {
  /** Whether the app is embedded (boolean) */
  embed: boolean;
  /** Source application or context */
  source: string | undefined;
  /** User alias for context */
  userAlias: string | undefined;
}

/**
 * Return type for useEmbedParams hook
 */
export interface UseEmbedParamsReturn {
  /** Raw string parameters from URL */
  rawParams: EmbedParams;
  /** Parsed parameters with proper types */
  parsedParams: ParsedEmbedParams;
  /** Convenience flag for embed status */
  isEmbedded: boolean;
}

/**
 * Helper function to convert string parameter to boolean
 */
const parseBoolean = (value: string | undefined): boolean => {
  return value === 'true';
};

/**
 * Helper function to get string parameter
 */
const parseString = (value: string | undefined): string | undefined => {
  return value || undefined;
};

/**
 * Custom hook to parse and manage embed-related URL parameters
 *
 * This hook extracts embed parameters from the URL query string and provides
 * both raw string values and properly typed parsed values.
 *
 * @returns Object containing raw params, parsed params, and embed status
 *
 * @example
 * ```tsx
 * const { isEmbedded, parsedParams } = useEmbedParams();
 *
 * if (isEmbedded) {
 *   console.log('Embedded from:', parsedParams.source);
 * }
 * ```
 */
export const useEmbedParams = (): UseEmbedParamsReturn => {
  // Use the shared parseEmbedParams helper for initial state — single source of truth
  const [rawParams, setRawParams] = useState<EmbedParams>(() => parseEmbedParams().rawParams);
  const [isEmbedded, setIsEmbedded] = useState(() => parseEmbedParams().isEmbedded);

  // Re-parse URL parameters on mount and when URL changes via browser navigation
  useEffect(() => {
    const syncFromUrl = () => {
      try {
        const { rawParams: params, isEmbedded: embedded } = parseEmbedParams();
        setRawParams(params);
        setIsEmbedded(embedded);
      } catch (error) {
        // Handle malformed URLs gracefully
        // This can occur if URL contains invalid characters or encoding
        logger.warn('Failed to parse URL parameters:', {
          error: error instanceof Error ? error.message : 'Unknown error',
        });
        setRawParams({});
        setIsEmbedded(false);
      }
    };

    // Parse params on mount
    syncFromUrl();

    // Listen for browser back/forward navigation
    // This ensures embed params are re-parsed when user navigates via browser controls
    const handlePopState = () => {
      syncFromUrl();
    };

    window.addEventListener('popstate', handlePopState);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // Note: Empty deps array is intentional - we only want to set up the listener once
    // The popstate event handler will be called whenever URL changes via browser navigation
  }, []);

  // Parse all parameters with proper types
  // useMemo to prevent unnecessary recreations
  const parsedParams = useMemo<ParsedEmbedParams>(
    () => ({
      embed: parseBoolean(rawParams.embed),
      source: parseString(rawParams.source),
      userAlias: parseString(rawParams.userAlias),
    }),
    [rawParams]
  );

  return {
    rawParams,
    parsedParams,
    isEmbedded,
  };
};
